import axios from 'axios'
import { API } from 'aws-amplify'

const getApiConfig = () => {
  // Get current hostname
  const hostname = window.location.hostname

  // Determine environment based on URL
  const isProduction = hostname === 'login.agentpay.app'
  const isStaging = hostname === 'staging.agentpay.app'

  // API version - easy to update in one place
  const apiVersion = 'v3'
  /// Test against, change this for which API test the direct API against, either test or 'live'
  const testAgainst = 'live'
  // Return appropriate configuration
  if (isProduction) {
    return {
      baseURL: `https://live-api.letmc.com/${apiVersion}/corporate/agentpay/letmcletting`,
      apiKey: 'kPeGFTrRpM1c7XUKWB8Kky8gox5dR3blrBveohuXkXQ1' // TODO: Replace with actual live API key
    }
  } else if (isStaging) {
    // Staging uses production URL for end-to-end testing
    return {
      baseURL: `https://live-api.letmc.com/${apiVersion}/corporate/agentpay/letmcletting`,
      apiKey: 'kPeGFTrRpM1c7XUKWB8Kky8gox5dR3blrBveohuXkXQ1'
    }
  } else {
    // Development or other environments
    if(testAgainst === 'live'){
      return {
        baseURL: `https://live-api.letmc.com/${apiVersion}/corporate/agentpay/letmcletting`,
        apiKey: 'kPeGFTrRpM1c7XUKWB8Kky8gox5dR3blrBveohuXkXQ1'
      }
    }
    else{
      return {
        baseURL: `https://test2-api.letmc.com/${apiVersion}/corporate/agentpay/letmcletting`,
        apiKey: 'yrR5n2TUHYH44ybHgYhGdre5k-ze6fUwLI7UTNzKjuI1'
      }
    }
  }
}

/**
 * Amplify API wrapper with proper URL encoding for proxy endpoints
 * This ensures AWS SigV4 signatures are calculated correctly when endpoints contain special characters
 */
const amplifyProxy = {
  /**
   * GET request through the proxy endpoint
   * @param {string} endpoint - The API endpoint to call (e.g., "/{clientName}/company/person/{id}/account")
   * @param {object} options - Additional options (headers, queryStringParameters, etc.)
   * @returns {Promise} Response from the API
   */
  async get(endpoint, options = {}) {
    const apiName = 'agentPayRestApi'
    const path = '/proxy/get'

    // URL-encode the endpoint to handle special characters (forward slashes, etc.)
    // This ensures proper AWS SigV4 signature calculation
    const encodedEndpoint = encodeURIComponent(endpoint)

    const myInit = {
      ...options,
      queryStringParameters: {
        endpoint: encodedEndpoint,
        ...(options.queryStringParameters || {})
      }
    }

    return API.get(apiName, path, myInit)
  },

  /**
   * POST request through the proxy endpoint
   * @param {string} endpoint - The API endpoint to call
   * @param {object} body - Request body
   * @param {object} options - Additional options (headers, queryStringParameters, etc.)
   * @returns {Promise} Response from the API
   */
  async post(endpoint, body, options = {}) {
    const apiName = 'agentPayRestApi'
    const path = '/proxy/post'

    const encodedEndpoint = encodeURIComponent(endpoint)

    const myInit = {
      ...options,
      body,
      queryStringParameters: {
        endpoint: encodedEndpoint,
        ...(options.queryStringParameters || {})
      }
    }

    return API.post(apiName, path, myInit)
  },

  /**
   * PUT request through the proxy endpoint
   * @param {string} endpoint - The API endpoint to call
   * @param {object} body - Request body
   * @param {object} options - Additional options (headers, queryStringParameters, etc.)
   * @returns {Promise} Response from the API
   */
  async put(endpoint, body, options = {}) {
    const apiName = 'agentPayRestApi'
    const path = '/proxy/put'

    const encodedEndpoint = encodeURIComponent(endpoint)

    const myInit = {
      ...options,
      body,
      queryStringParameters: {
        endpoint: encodedEndpoint,
        ...(options.queryStringParameters || {})
      }
    }

    return API.put(apiName, path, myInit)
  },

  /**
   * DELETE request through the proxy endpoint
   * @param {string} endpoint - The API endpoint to call
   * @param {object} options - Additional options (headers, queryStringParameters, etc.)
   * @returns {Promise} Response from the API
   */
  async del(endpoint, options = {}) {
    const apiName = 'agentPayRestApi'
    const path = '/proxy/delete'

    const encodedEndpoint = encodeURIComponent(endpoint)

    const myInit = {
      ...options,
      queryStringParameters: {
        endpoint: encodedEndpoint,
        ...(options.queryStringParameters || {})
      }
    }

    return API.del(apiName, path, myInit)
  },

  /**
   * PATCH request through the proxy endpoint
   * @param {string} endpoint - The API endpoint to call
   * @param {object} body - Request body
   * @param {object} options - Additional options (headers, queryStringParameters, etc.)
   * @returns {Promise} Response from the API
   */
  async patch(endpoint, body, options = {}) {
    const apiName = 'agentPayRestApi'
    const path = '/proxy/patch'

    const encodedEndpoint = encodeURIComponent(endpoint)

    const myInit = {
      ...options,
      body,
      queryStringParameters: {
        endpoint: encodedEndpoint,
        ...(options.queryStringParameters || {})
      }
    }

    return API.patch(apiName, path, myInit)
  }
}

export const directApi = {
  async addInvoice(clientName, invoiceData, token) {
    const config = getApiConfig()

    if (!config.baseURL || !config.apiKey) {
      throw new Error('Direct API configuration is missing. Please set VUE_APP_DIRECT_API_BASE_URL and VUE_APP_DIRECT_API_KEY')
    }

    const url = `${config.baseURL}/${clientName}/contractors/deductions`

    try {
      const response = await axios.post(url, invoiceData, {
        headers: {
          'Content-Type': 'application/json',
          'api_key': config.apiKey,
          'token': token,
          'authorization': token
        }
      })

      return response.data
    } catch (error) {
      console.error('Direct API error:', error)
      throw error
    }
  },

  async downloadDocument(clientName, documentID, filename, token) {
    const config = getApiConfig()

    if (!config.baseURL || !config.apiKey) {
      throw new Error('Direct API configuration is missing')
    }

    const url = `${config.baseURL}/${clientName}/download/document`

    try {
      const response = await axios.get(url, {
        params: {
          documentID: documentID
        },
        headers: {
          'api_key': config.apiKey,
          'token': token,
          'authorization': token,
          'Accept': 'application/octet-stream'
        },
        responseType: 'blob' // CRITICAL: Tell axios to expect binary data
      })

      // Determine MIME type from filename
      const extension = filename.split('.').pop().toLowerCase()
      const mimeTypes = {
        'pdf': 'application/pdf',
        'doc': 'application/msword',
        'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'png': 'image/png',
        'jpg': 'image/jpeg',
        'jpeg': 'image/jpeg'
      }
      const contentType = mimeTypes[extension] || 'application/octet-stream'

      // Create blob and trigger download
      const blob = new Blob([response.data], { type: contentType })
      const fileURL = window.URL.createObjectURL(blob)
      const fileLink = document.createElement('a')

      fileLink.href = fileURL
      fileLink.setAttribute('download', filename)
      document.body.appendChild(fileLink)
      fileLink.click()

      // Cleanup
      document.body.removeChild(fileLink)
      window.URL.revokeObjectURL(fileURL)

      return { success: true }
    } catch (error) {
      console.error('Download error:', error)
      throw error
    }
  },

  async downloadInvoice(clientName, invoiceID, filename, token) {
    const config = getApiConfig()

    if (!config.baseURL || !config.apiKey) {
      throw new Error('Direct API configuration is missing')
    }

    const url = `${config.baseURL}/${clientName}/download/invoice`

    try {
      const response = await axios.get(url, {
        params: {
          invoiceID: invoiceID
        },
        headers: {
          'api_key': config.apiKey,
          'token': token,
          'authorization': token,
          'Accept': 'application/octet-stream'
        },
        responseType: 'blob'
      })

      const blob = new Blob([response.data], { type: 'application/pdf' })
      const fileURL = window.URL.createObjectURL(blob)
      const fileLink = document.createElement('a')

      fileLink.href = fileURL
      fileLink.setAttribute('download', filename)
      document.body.appendChild(fileLink)
      fileLink.click()

      // Cleanup
      document.body.removeChild(fileLink)
      window.URL.revokeObjectURL(fileURL)

      return { success: true }
    } catch (error) {
      console.error('Download error:', error)
      throw error
    }
  },

  async sendComment(clientName, attachments, queryParams, token) {
    const config = getApiConfig()

    if (!config.baseURL || !config.apiKey) {
      throw new Error('Direct API configuration is missing')
    }

    const url = `${config.baseURL}/${clientName}/messaging/comment/submit`

    try {
      const response = await axios.post(url, attachments, {
        params: queryParams,
        headers: {
          'Content-Type': 'application/json',
          'api_key': config.apiKey,
          'token': token,
          'authorization': token
        }
      })

      return response.data
    } catch (error) {
      console.error('Direct API sendComment error:', error)
      throw error
    }
  },

  async exportCsv(clientName, endpoint, queryParams, filename, token) {
    const config = getApiConfig()

    if (!config.baseURL || !config.apiKey) {
      throw new Error('Direct API configuration is missing')
    }

    const url = `${config.baseURL}${endpoint}`

    try {
      const response = await axios.get(url, {
        params: queryParams,
        headers: {
          'api_key': config.apiKey,
          'token': token,
          'authorization': token,
          'Accept': 'text/csv'
        },
        responseType: 'text'
      })

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' })
      const fileURL = window.URL.createObjectURL(blob)
      const fileLink = document.createElement('a')

      fileLink.href = fileURL
      fileLink.setAttribute('download', filename)
      document.body.appendChild(fileLink)
      fileLink.click()

      document.body.removeChild(fileLink)
      window.URL.revokeObjectURL(fileURL)

      return { success: true }
    } catch (error) {
      console.error('CSV export error:', error)
      throw error
    }
  }
}

// Export the Amplify proxy wrapper
export { amplifyProxy }